import React, { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import {
  FaHome,
  FaChartLine,
  FaCoins,
  FaCalendarAlt,
  FaTools,
  FaMobileAlt,
  FaDiscord,
  FaTwitter,
  FaTelegramPlane,
  FaYoutube,
} from 'react-icons/fa'
import './style/TradePortal.scss'

// Dummy data
const navLinks = [
  { label: 'Home', icon: <FaHome />, to: '/' },
  { label: 'Markets', to: '/markets' },
  { label: 'Trade', to: '/trade' },
  { label: 'Derivatives', to: '/derivatives' },
  { label: 'Earn', to: '/earn' },
  { label: 'NFTs', to: '/nfts' },
  { label: 'Institutional', to: '/institutional' },
  { label: 'More', to: '/more' },
]

const listings = [
  { pair: 'BTC/USDT', price: '29,450', change: '+2.1%' },
  { pair: 'ETH/USDT', price: '1,850', change: '-1.3%' },
  { pair: 'BNB/USDT', price: '320', change: '+0.8%' },
  { pair: 'SOL/USDT', price: '22.30', change: '+4.5%' },
]

// ① Define a MarketTab union
type MarketTab = 'Spot' | 'Futures' | 'Options'

// ② Tell TS marketTabs is an array of that union
const marketTabs: MarketTab[] = ['Spot', 'Futures', 'Options']

// ③ Type the marketData object as a Record mapping each MarketTab
const marketData: Record<MarketTab, { pair: string; change: string }[]> = {
  Spot: [
    { pair: 'XRP/USDT', change: '+3.2%' },
    { pair: 'DOGE/USDT', change: '-0.5%' },
    { pair: 'ADA/USDT', change: '+1.8%' },
  ],
  Futures: [
    { pair: 'BTC-Perp', change: '+1.1%' },
    { pair: 'ETH-Perp', change: '-0.2%' },
  ],
  Options: [
    { pair: 'BTC-30JUN24', change: '+5.0%' },
    { pair: 'ETH-30JUN24', change: '+2.3%' },
  ],
}


const instruments = [
  { title: 'Spot Grid', icon: <FaTools />, desc: 'Automated spot grid trading bot' },
  { title: 'Futures Grid', icon: <FaTools />, desc: 'Set your futures grid strategies' },
  { title: 'DCA', icon: <FaCalendarAlt />, desc: 'Dollar-cost averaging tool' },
  { title: 'Martingale', icon: <FaTools />, desc: 'Futures Martingale strategy' },
]

const innovations = [
  'Unified Trading Account',
  'Demo Trading Guide',
  'OTC Trading',
  'MetaTrader 5 Integration',
  'Crypto Loans',
  'Pre-Market',
]

const programs = [
  'VIP Program',
  'Institutional Services',
  'Market Maker Program',
  'Broker Program',
]

export const TradePortal: React.FC = () => {
  const [activeMarket, setActiveMarket] = useState<MarketTab>('Spot')

  const [countdown, setCountdown] = useState<number>(24 * 60 * 60) // seconds

  // Simple countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 24 * 60 * 60))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const formatTime = (sec: number) => {
    const h = Math.floor(sec / 3600).toString().padStart(2, '0')
    const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0')
    const s = (sec % 60).toString().padStart(2, '0')
    return `${h}:${m}:${s}`
  }

  return (
    <div className="trade-portal">
    

      {/* Hero Banner */}
      <section className="tp-hero">
        <h1>Start Your Crypto Journey</h1>
        <p>Total Trading Volume: $36B+</p>
        <div className="hero-actions">
          <button>Trade Spot</button>
          <button>Trade Derivatives</button>
          <button className="demo">Demo Trade</button>
        </div>
      </section>

      {/* Daily Treasure Hunt */}
      <section className="tp-event">
        <div className="event-content">
          <h2>Daily Treasure Hunt</h2>
          <p>Win guaranteed prizes every day!</p>
          <div className="countdown">{formatTime(countdown)}</div>
        </div>
      </section>

      {/* Latest Listings */}
      <section className="tp-listings">
        <h2>Latest Listings</h2>
        <table>
          <thead>
            <tr>
              <th>Pair</th>
              <th>Last Price</th>
              <th>24h Change</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.pair}>
                <td>{l.pair}</td>
                <td>${l.price}</td>
                <td className={l.change.startsWith('+') ? 'up' : 'down'}>
                  {l.change}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Market Dynamics */}
      <section className="tp-market">
        <h2>Live Market Dynamics</h2>
        <div className="tabs">
          {marketTabs.map((tab) => (
            <button
              key={tab}
              className={activeMarket === tab ? 'active' : ''}
              onClick={() => setActiveMarket(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <ul className="market-list">
  {marketData[activeMarket].map((m) => (
    <li key={m.pair}>
      <span>{m.pair}</span>
      <span className={m.change.startsWith('+') ? 'up' : 'down'}>
        {m.change}
      </span>
    </li>
  ))}
</ul>

      </section>

      {/* Trading Instruments */}
      <section className="tp-instruments">
        <h2>Diverse Trading Instruments</h2>
        <div className="grid">
          {instruments.map((inst) => (
            <div key={inst.title} className="card">
              <div className="icon">{inst.icon}</div>
              <h3>{inst.title}</h3>
              <p>{inst.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Smart Tools */}
      <section className="tp-tools">
        <h2>Trade Smart with Powerful Tools</h2>
        <div className="grid">
          {instruments.map((tool) => (
            <div key={tool.title} className="card">
              <div className="icon">{tool.icon}</div>
              <h3>{tool.title}</h3>
              <p>{tool.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Innovations */}
      <section className="tp-innovations">
        <h2>Featured Trading Innovations</h2>
        <ul>
          {innovations.map((feat) => (
            <li key={feat}>{feat}</li>
          ))}
        </ul>
      </section>

      {/* Exclusive Programs */}
      <section className="tp-programs">
        <h2>Exclusive Programs</h2>
        <ul>
          {programs.map((prog) => (
            <li key={prog}>{prog}</li>
          ))}
        </ul>
      </section>

      {/* Mobile App Download */}
      <section className="tp-download">
        <h2>Trade On-the-Go</h2>
        <p>Download our app for iOS & Android</p>
        <div className="apps">
          <a href="#"><FaMobileAlt /> App Store</a>
          <a href="#"><FaMobileAlt /> Google Play</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="tp-footer">
        <div className="socials">
          <FaDiscord />
          <FaTelegramPlane />
          <FaTwitter />
          <FaYoutube />
        </div>
        <div className="links">
          <NavLink to="/about">About</NavLink>
          <NavLink to="/support">Support</NavLink>
          <NavLink to="/services">Services</NavLink>
        </div>
        <p>© 2025 MyExchange. All rights reserved.</p>
      </footer>
    </div>
  )
}
export default TradePortal